import PropTypes from 'prop-types';
import { BsXCircle } from 'react-icons/bs';

import Card from '#globalComponents/Card/Card';
import ProgressBar from '#globalComponents/ProgressBar/ProgressBar';

import { LOW_PROBABILITY_MAX, MEDIUM_PROBABILITY_MAX } from '../../../constants/dealThresholds';
import { formatEuro, formatLossReason } from '../../../utils/formatters';

import ActivityPopover from './ActivityPopover';
import OwnerDropdown from './OwnerDropdown';

function getProbabilityTone(probability) {
	if (probability < LOW_PROBABILITY_MAX) return 'warning';
	if (probability < MEDIUM_PROBABILITY_MAX) return 'caution';
	return 'success';
}

/** Dragging is handled by the parent SortableDealCard wrapper. */
const DealCard = ({ deal, onOpen, isDragging = false, dragAttributes, dragListeners, setNodeRef, style }) => {
	const probability = deal.closingChance !== null ? Math.round(Number(deal.closingChance) * 100) : null;
	// Only surfaced on lost deals — the reason is cleared when a deal reopens.
	const lossReasonLabel = deal.status === 'lost' ? formatLossReason(deal) : null;

	return (
		<Card
			ref={setNodeRef}
			style={{ touchAction: 'none', ...style }}
			className={`space-y-3 bg-bgBase-300 transition-colors ${
				isDragging ? 'opacity-50' : 'cursor-pointer hover:border-primary-300'
			}`}
			onClick={() => !isDragging && deal.crmCompanyId && onOpen?.(deal.crmCompanyId)}
			{...dragAttributes}
			{...dragListeners}
		>
			<div>
				<h4 className="text-md-bold text-text break-words">{deal.title}</h4>
				<span className="block truncate text-sm text-bgBase-800">{deal.companyNameSnapshot}</span>
			</div>

			{/* Falls back to the note when the org keeps the loss category optional. */}
			{lossReasonLabel && (
				<div
					className="flex items-start gap-1.5 rounded-control bg-warning-200 px-2 py-1 text-xs text-warning-700"
					title={lossReasonLabel}
				>
					<BsXCircle className="mt-[2px] h-3 w-3 flex-shrink-0" />
					<span className="break-words line-clamp-2">{deal.lossReason || deal.lossReasonNote}</span>
				</div>
			)}

			<div className="flex items-center justify-between gap-3 text-sm text-bgBase-800 tabular-nums">
				<span>{formatEuro(deal.dealValue)}</span>
				{probability !== null && (
					<div className="flex items-center gap-2" title={`${probability}% Wahrscheinlichkeit`}>
						<ProgressBar
							value={probability}
							max={100}
							tone={getProbabilityTone(probability)}
							className="h-1.5 w-14 bg-white"
						/>
						<span>{probability}%</span>
					</div>
				)}
			</div>

			<div className="flex items-center justify-between">
				<OwnerDropdown deal={deal} />
				<ActivityPopover deal={deal} probability={probability} />
			</div>
		</Card>
	);
};

DealCard.propTypes = {
	deal: PropTypes.object.isRequired,
	onOpen: PropTypes.func,
	isDragging: PropTypes.bool,
	dragAttributes: PropTypes.object,
	dragListeners: PropTypes.object,
	setNodeRef: PropTypes.func,
	style: PropTypes.object,
};

export default DealCard;
